import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { testimonials } from '../mock';

export const TestimonialSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const itemsToShow = 3; // Show 3 reviews at a time
  const totalSlides = testimonials.length; // One slide per testimonial for continuous loop

  useEffect(() => {
    const timer = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  const goToPrevious = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }
  };

  const goToNext = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating && index !== currentIndex) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex(index);
        setIsAnimating(false);
      }, 100);
    }
  };

  // Get 3 testimonials starting from currentIndex, wrapping around if needed
  const getCurrentTestimonials = () => {
    const items = [];
    for (let i = 0; i < itemsToShow; i++) {
      const index = (currentIndex + i) % testimonials.length;
      items.push(testimonials[index]);
    }
    return items;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block bg-blue-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
            TESTIMONIALS
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
          <p className="text-lg text-gray-600">Trusted by businesses nationwide</p>
        </div>

        <div className="max-w-7xl mx-auto relative">
          {/* Desktop - 3 cards with slide animation */}
          <div className="hidden md:block overflow-hidden">
            <div 
              className={`grid grid-cols-3 gap-6 transition-all duration-700 ease-in-out ${
                isAnimating ? 'opacity-0 transform translate-x-8' : 'opacity-100 transform translate-x-0'
              }`}
            >
              {getCurrentTestimonials().map((testimonial, idx) => (
                <div 
                  key={`${testimonial.id}-${currentIndex}-${idx}`} 
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <Quote className="text-primary/20" size={40} />
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-base text-gray-700 mb-6 leading-relaxed line-clamp-4">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-primary font-medium">{testimonial.designation}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile - 1 card with slide animation */}
          <div className="md:hidden overflow-hidden">
            <div 
              className={`transition-all duration-700 ease-in-out ${
                isAnimating ? 'opacity-0 transform translate-x-8' : 'opacity-100 transform translate-x-0'
              }`}
            >
              {getCurrentTestimonials().slice(0, 1).map((testimonial) => (
                <div key={`${testimonial.id}-${currentIndex}-mobile`} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex justify-between items-start mb-4">
                    <Quote className="text-primary/20" size={40} />
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-base text-gray-700 mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-primary font-medium">{testimonial.designation}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 bg-white hover:bg-primary hover:text-white p-3 rounded-full shadow-lg transition-all border-2 border-gray-200 hover:border-primary"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 bg-white hover:bg-primary hover:text-white p-3 rounded-full shadow-lg transition-all border-2 border-gray-200 hover:border-primary"
          >
            <ChevronRight size={24} />
          </button>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(totalSlides)].map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-primary w-8' : 'bg-gray-300 w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};